import { ColumnDef } from '@tanstack/react-table';
import { ArrowRight, Ellipsis, Trash2 } from 'lucide-react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { PROFILE_SOURCES } from '../../../shared/candidate-form-schema';
import { CommentCell, sourceColumnFilter } from '../../../shared/components/candidate-cells';
import { PotentialProfile } from '../../types/profile.types';

interface CreateProfileTableColumnsProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  /** The internal referrers' names of a profile, or '' when none (or not loaded yet). */
  referrersOf: (profile: PotentialProfile) => string;
  onTransfer: (profile: PotentialProfile) => void;
  onDelete: (profile: PotentialProfile) => void;
}

/**
 * Candidates (HR Module Prototype `hiringPage("candidates")`): Name, Phone number, Email, Years of
 * experience, Designation, Source, Internal referral, Comments, and a row menu with Transfer to offers and
 * Delete. The prototype's CV column waits for CV upload (not stored yet).
 */
export const createProfileTableColumns = ({
  t,
  referrersOf,
  onTransfer,
  onDelete,
}: CreateProfileTableColumnsProps): ColumnDef<PotentialProfile>[] => [
  {
    // Shows the full name; sorts and filters by first name.
    id: 'FirstName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
    accessorFn: (row) => `${row.FirstName} ${row.LastName}`,
    cell: ({ getValue }) => <span className="font-medium">{getValue<string>()}</span>,
  },
  {
    id: 'PhoneNumber',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PHONE_NUMBER')} />,
    accessorFn: (row) => row.PhoneNumber,
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap tabular-nums">{getValue<string>()}</span>
    ),
  },
  {
    id: 'Email',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    accessorFn: (row) => row.Email,
  },
  {
    id: 'YearsOfExperience',
    meta: { filter: { type: 'number' }, numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('YEARS_OF_EXPERIENCE')} />
    ),
    accessorFn: (row) => row.YearsOfExperience,
  },
  {
    id: 'Designation',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESIGNATION')} />,
    accessorFn: (row) => row.Designation,
  },
  {
    id: 'Source',
    meta: { filter: sourceColumnFilter(PROFILE_SOURCES) },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SOURCE')} />,
    accessorFn: (row) => row.Source,
  },
  {
    id: 'InternalReferral',
    // Names looked up from the referrers' user ids; not a field the server can sort on.
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('INTERNAL_REFERRAL')} />
    ),
    accessorFn: (row) => referrersOf(row) || '—',
  },
  {
    id: 'Comment',
    // Free text (SORT-01: description columns aren't sortable).
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('COMMENT')} />,
    accessorFn: (row) => row.Comment ?? '',
    cell: ({ getValue }) => <CommentCell text={getValue<string>()} />,
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const name = `${row.original.FirstName} ${row.original.LastName}`;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('ACTIONS_FOR', { name })}
              title={t('ACTIONS')}
              onClick={(event) => event.stopPropagation()}
            >
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onTransfer(row.original)}>
              <ArrowRight />
              {t('TRANSFER_TO_OFFERS')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => onDelete(row.original)}>
              <Trash2 />
              {t('DELETE')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
